import React from "react";
import {
  Send,
  Gift,
  Smartphone,
  Banknote,
  Tablet,
  GraduationCap,
  Gem,
} from "lucide-react";
import { LINKS, ASSETS } from "@/lib/links";
import usePageMeta from "@/hooks/usePageMeta";
import VideoBackground from "@/components/VideoBackground";
import PageNav from "@/components/PageNav";
import ScrollProgress from "@/components/ScrollProgress";
import GiveawayCountdown from "@/components/giveaway/GiveawayCountdown";
import GiveawayFeedbackCarousel from "@/components/giveaway/GiveawayFeedbackCarousel";

const GIVEAWAY_CHANNEL = LINKS.giveawayChannel;
const CURRENT_MEMBERS = 1267;
const TARGET_MEMBERS = 2000;
const MILESTONE_PCT = Math.min(100, (CURRENT_MEMBERS / TARGET_MEMBERS) * 100);

const PRIZE_TYPES = [
  { icon: Banknote, label: "Cash" },
  { icon: Smartphone, label: "Gadget" },
  { icon: Tablet, label: "iPad" },
  { icon: GraduationCap, label: "Coaching" },
  { icon: Gem, label: "Gold Bar" },
];

const STEPS = [
  "Join channel @GiveawayLuqmanan",
  "Tunggu announcement setiap Ahad",
  "Ikut cara join giveaway (percuma)",
];

export default function GiveawayPage() {
  usePageMeta({
    title: "LUQMANAN | Giveaway",
    description:
      "Giveaway percuma setiap minggu. Mystery prize, announce Ahad. Join @GiveawayLuqmanan.",
  });

  return (
    <VideoBackground data-testid="giveaway-page">
      <ScrollProgress />

      <PageNav
        ctaHref={GIVEAWAY_CHANNEL}
        ctaLabel="Join Giveaway"
        ctaTestId="nav-giveaway-cta"
      />

      <main className="pt-16 sm:pt-20 pb-12">
        {/* Hero banner */}
        <section id="giveaway-hero" className="relative z-10 px-4 py-6 sm:py-8">
          <div className="max-w-2xl mx-auto">
            <div className="giveaway-banner-wrap game-panel p-1.5 sm:p-2">
              <img
                src={ASSETS.giveawayBanner}
                alt="LUQMANAN Giveaway — Cash, Gadget, iPad, Coaching, Gold Bar"
                className="giveaway-banner-img w-full h-auto rounded-sm"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </div>

            <div className="max-w-md mx-auto mt-4 text-center">
              <span className="game-tag">Percuma · No payment</span>
              <p className="text-[#8B9CC7] text-xs sm:text-sm mt-2 leading-relaxed">
                Mystery prize confirm mahal. Announce setiap Ahad di channel.
                Join sekarang, tunggu announcement je.
              </p>

              <a
                href={GIVEAWAY_CHANNEL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="giveaway-hero-cta"
                className="game-btn-primary inline-flex items-center justify-center gap-2 w-full mt-4 px-5 py-3 text-xs sm:text-sm"
              >
                <Send className="w-4 h-4" />
                Join @GiveawayLuqmanan
              </a>
            </div>
          </div>
        </section>

        {/* Countdown */}
        <section className="relative z-10 px-4 pb-6">
          <div className="max-w-md mx-auto game-panel px-4 py-4 text-center">
            <GiveawayCountdown />
          </div>
        </section>

        {/* Milestone */}
        <section id="milestone" className="relative z-10 px-4 py-6">
          <div className="max-w-md mx-auto game-panel px-4 py-5">
            <div className="text-center mb-4">
              <span className="game-tag">Kenapa wajib join?</span>
              <h2 className="font-display text-base sm:text-lg font-bold text-white mt-2">
                Prize pool unlock bila capai 2K
              </h2>
            </div>

            <div className="giveaway-milestone-bar" aria-hidden="true">
              <div
                className="giveaway-milestone-bar__fill"
                style={{ width: `${MILESTONE_PCT}%` }}
              />
            </div>

            <div className="flex justify-between items-baseline mt-2">
              <span className="font-display text-lg font-bold text-[#E9FF00]">
                {CURRENT_MEMBERS.toLocaleString()}
              </span>
              <span className="text-[#8B9CC7] text-xs">
                / {TARGET_MEMBERS.toLocaleString()} members
              </span>
            </div>

            <p className="text-[#8B9CC7] text-xs sm:text-sm mt-3 text-center leading-relaxed">
              {(TARGET_MEMBERS - CURRENT_MEMBERS).toLocaleString()} lagi untuk unlock
              prize pool level baru. Share channel, jom capai sama-sama.
            </p>
          </div>
        </section>

        {/* Prize types */}
        <section id="prizes" className="relative z-10 px-4 py-6">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-4">
              <span className="game-tag">Mystery</span>
              <h2 className="font-display text-base sm:text-lg font-bold text-white mt-2">
                Antara prize minggu ini
              </h2>
              <p className="text-[#8B9CC7] text-xs mt-1">Reveal penuh setiap Ahad</p>
            </div>

            <div className="giveaway-prize-grid">
              {PRIZE_TYPES.map(({ icon: Icon, label }) => (
                <div key={label} className="giveaway-prize-card">
                  <Icon className="w-5 h-5 text-[#00A3FF] mb-2" />
                  <span className="font-display text-sm font-bold text-white">
                    {label}
                  </span>
                  <span className="text-[10px] text-[#8B9CC7] uppercase tracking-wider mt-0.5">
                    Mystery
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how" className="relative z-10 px-4 py-6">
          <div className="max-w-md mx-auto game-panel px-4 py-5">
            <div className="text-center mb-4">
              <span className="game-tag">Cara join</span>
              <h2 className="font-display text-base sm:text-lg font-bold text-white mt-2">
                3 step je
              </h2>
            </div>

            <ol className="giveaway-steps">
              {STEPS.map((step, i) => (
                <li key={step} className="giveaway-step">
                  <span className="giveaway-step__num font-display">{i + 1}</span>
                  <span className="giveaway-step__text">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <GiveawayFeedbackCarousel />

        {/* Final CTA */}
        <section id="giveaway-cta" className="relative z-10 px-4 py-8">
          <div className="max-w-md mx-auto game-panel px-4 py-6 text-center">
            <Gift className="w-8 h-8 text-[#E9FF00] mx-auto mb-3" />
            <h2 className="font-display text-lg sm:text-xl font-bold text-white">
              Jom join sekarang
            </h2>
            <p className="text-[#8B9CC7] text-xs sm:text-sm mt-2 mb-4">
              Percuma. Tak perlu bayar apa-apa. Mystery prize confirm mahal.
            </p>
            <a
              href={GIVEAWAY_CHANNEL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="giveaway-final-cta"
              className="game-btn-primary inline-flex items-center justify-center gap-2 w-full px-5 py-3 text-xs sm:text-sm"
            >
              <Send className="w-4 h-4" />
              Join @GiveawayLuqmanan
            </a>
          </div>
        </section>
      </main>
    </VideoBackground>
  );
}
